import { clamp, DEG, type Point, type Rect, type Size } from '../../doc/geometry';

/**
 * Screen offset of the world origin (px), zoom, and the canvas rotation in degrees (clockwise,
 * about the world origin). screen = rotate(world * zoom, rot) + (x, y).
 */
export interface Camera {
  x: number;
  y: number;
  zoom: number;
  rot: number;
}

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 16;

/** The canvas rotation snaps to these steps, within ROT_SNAP_RANGE degrees of one. */
export const ROT_SNAP_STEP = 45;
export const ROT_SNAP_RANGE = 6;

export const screenToWorld = (c: Camera, p: Point): Point => {
  const dx = p.x - c.x;
  const dy = p.y - c.y;
  if (!c.rot) return { x: dx / c.zoom, y: dy / c.zoom };
  const cos = Math.cos(c.rot * DEG);
  const sin = Math.sin(c.rot * DEG);
  return { x: (dx * cos + dy * sin) / c.zoom, y: (dy * cos - dx * sin) / c.zoom };
};

export const worldToScreen = (c: Camera, p: Point): Point => {
  if (!c.rot) return { x: p.x * c.zoom + c.x, y: p.y * c.zoom + c.y };
  const cos = Math.cos(c.rot * DEG);
  const sin = Math.sin(c.rot * DEG);
  return { x: (p.x * cos - p.y * sin) * c.zoom + c.x, y: (p.x * sin + p.y * cos) * c.zoom + c.y };
};

/** Zoom so that the world point under `screenPt` stays put. */
export function zoomAt(c: Camera, screenPt: Point, newZoom: number): Camera {
  const zoom = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
  const k = zoom / c.zoom;
  return {
    x: screenPt.x - (screenPt.x - c.x) * k,
    y: screenPt.y - (screenPt.y - c.y) * k,
    zoom,
    rot: c.rot,
  };
}

/** Degrees folded into [0, 360). */
export const normalizeRot = (deg: number): number => {
  const r = ((deg % 360) + 360) % 360;
  return r === 360 ? 0 : r;
};

/** The nearest 45 degree step when `deg` is close enough to one, else `deg` itself. */
export function snapRot(deg: number): number {
  const step = Math.round(deg / ROT_SNAP_STEP) * ROT_SNAP_STEP;
  return Math.abs(deg - step) <= ROT_SNAP_RANGE ? step : deg;
}

/** Rotate the canvas to `rot` so that the world point under `screenPt` stays put. */
export function rotateAt(c: Camera, screenPt: Point, rot: number): Camera {
  const next = normalizeRot(rot);
  const d = (next - c.rot) * DEG;
  if (!d) return c;
  const cos = Math.cos(d);
  const sin = Math.sin(d);
  const dx = c.x - screenPt.x;
  const dy = c.y - screenPt.y;
  return { x: screenPt.x + dx * cos - dy * sin, y: screenPt.y + dx * sin + dy * cos, zoom: c.zoom, rot: next };
}

export const panBy = (c: Camera, dx: number, dy: number): Camera => ({
  x: c.x + dx,
  y: c.y + dy,
  zoom: c.zoom,
  rot: c.rot,
});

/** World-space bounds of what the viewport shows (of its four corners, once the canvas is rotated). */
export function visibleWorldRect(c: Camera, size: Size): Rect {
  const tl = screenToWorld(c, { x: 0, y: 0 });
  if (!c.rot) return { x: tl.x, y: tl.y, w: size.w / c.zoom, h: size.h / c.zoom };
  const br = screenToWorld(c, { x: size.w, y: size.h });
  const tr = screenToWorld(c, { x: size.w, y: 0 });
  const bl = screenToWorld(c, { x: 0, y: size.h });
  const x = Math.min(tl.x, tr.x, br.x, bl.x);
  const y = Math.min(tl.y, tr.y, br.y, bl.y);
  return { x, y, w: Math.max(tl.x, tr.x, br.x, bl.x) - x, h: Math.max(tl.y, tr.y, br.y, bl.y) - y };
}

/**
 * A world rect as a screen-space box: `left/top/width/height` of the unrotated box around its
 * centre, to be turned by `transform: rotate(rot)` about that centre.
 */
export function screenBox(c: Camera, rect: Rect): { left: number; top: number; width: number; height: number } {
  const sc = worldToScreen(c, { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 });
  const width = rect.w * c.zoom;
  const height = rect.h * c.zoom;
  return { left: sc.x - width / 2, top: sc.y - height / 2, width, height };
}

/** Axis-aligned screen bounds of a world rect. */
export function screenBounds(c: Camera, rect: Rect): Rect {
  const b = screenBox(c, rect);
  const cos = Math.abs(Math.cos(c.rot * DEG));
  const sin = Math.abs(Math.sin(c.rot * DEG));
  const w = b.width * cos + b.height * sin;
  const h = b.width * sin + b.height * cos;
  return { x: b.left + (b.width - w) / 2, y: b.top + (b.height - h) / 2, w, h };
}

export function fitRect(rect: Rect, size: Size, padding = 64, maxZoom = 1, rot = 0): Camera {
  const availW = Math.max(1, size.w - padding * 2);
  const availH = Math.max(1, size.h - padding * 2);
  const cos = Math.abs(Math.cos(rot * DEG));
  const sin = Math.abs(Math.sin(rot * DEG));
  const w = rect.w * cos + rect.h * sin;
  const h = rect.w * sin + rect.h * cos;
  const zoom = clamp(Math.min(availW / Math.max(w, 1), availH / Math.max(h, 1)), MIN_ZOOM, maxZoom);
  return centerOn({ x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 }, size, zoom, rot);
}

export function centerOn(worldPt: Point, size: Size, zoom: number, rot = 0): Camera {
  const p = worldToScreen({ x: 0, y: 0, zoom, rot }, worldPt);
  return { x: size.w / 2 - p.x, y: size.h / 2 - p.y, zoom, rot };
}
